import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import api from "../lib/api";
import { formatIntakeValue } from "../lib/intakeSelections";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, AlertOctagon, Clock, FileWarning, Hash, ChevronLeft,
  Star, FileX, Pause, Send, CheckCircle2, MessageSquare, Flag, Crown, XCircle,
} from "lucide-react";

const ALL_STATUSES = [
  "Draft", "Profile Submitted", "KYC Pending", "KYC Complete",
  "Accreditation Pending", "Accreditation Complete",
  "Data Transfer Consent Required", "Ready for Regulated Partner Review",
  "Transferred to Regulated Partner", "Partner Onboarding Pending",
  "Partner Onboarding Complete", "More Information Required",
  "Compliance Attention Required", "Priority Review", "Red Alert",
  "Suspended / Review Pending", "Rejected / Closed",
];

const ACTION_BUTTONS = [
  { id: "mark_priority",            label: "Mark Priority",         icon: Star,          tone: "amber" },
  { id: "mark_red_alert",           label: "Mark Red Alert",        icon: ShieldAlert,   tone: "red", needsReason: true },
  { id: "clear_red_alert",          label: "Clear Red Alert",       icon: CheckCircle2,  tone: "emerald" },
  { id: "request_more_information", label: "Request More Info",     icon: MessageSquare, tone: "blue", needsClientMessage: true, notifiable: true },
  { id: "escalate_to_founder",      label: "Escalate to Founder",   icon: Crown,         tone: "violet", needsReason: true },
  { id: "escalate_to_compliance",   label: "Escalate Compliance",   icon: Flag,          tone: "red",    needsReason: true },
  { id: "pause_file",               label: "Pause File",            icon: Pause,         tone: "amber",  needsReason: true, notifiable: true },
  { id: "approve_for_partner_handoff", label: "Approve for Partner Handoff", icon: Send, tone: "emerald", needsReason: false, notifiable: true },
  { id: "reject_close_file",        label: "Reject / Close",        icon: XCircle,      tone: "red",    needsReason: true, notifiable: true, needsClientReason: true },
  { id: "reopen_file",              label: "Reopen File",           icon: CheckCircle2, tone: "emerald", needsReason: true, notifiable: true },
  { id: "add_admin_note",           label: "Add Admin Note",        icon: MessageSquare, tone: "slate",  needsNotes: true },
  { id: "add_client_facing_note",   label: "Add Client-Facing Note", icon: MessageSquare, tone: "blue",  needsClientMessage: true },
];

const TONE_BTN = {
  red:     "bg-red-50 text-red-800 border-red-300 hover:bg-red-100",
  amber:   "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100",
  blue:    "bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100",
  violet:  "bg-violet-50 text-violet-800 border-violet-300 hover:bg-violet-100",
  slate:   "bg-slate-50 text-slate-800 border-slate-300 hover:bg-slate-100",
};

const AdminCommandCenterFilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionOpen, setActionOpen] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [clientMessage, setClientMessage] = useState("");
  const [statusOverride, setStatusOverride] = useState("");
  const [notifyClient, setNotifyClient] = useState(true);
  const [clientReason, setClientReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/command-center/admin/files/${userId}`),
        api.get(`/command-center/admin/files/${userId}/audit`),
      ]);
      setData(r1.data);
      setAudit(r2.data.entries || []);
    } catch (e) {
      toast.error("Failed to load file");
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const performAction = async (actionId, extraFields = {}) => {
    setSubmitting(true);
    try {
      const payload = {
        action: actionId,
        reason: reason || undefined,
        notes: notes || undefined,
        client_facing_message: clientMessage || undefined,
        client_facing_reason: clientReason || undefined,
        notify_client: !!(ACTION_BUTTONS.find((a) => a.id === actionId)?.notifiable && notifyClient),
        ...extraFields,
      };
      const r = await api.post(`/command-center/admin/files/${userId}/action`, payload);
      if (r.data?.notify_error) {
        // member-notify-001: the action stood, but the member email failed —
        // the audit log records it; make sure the admin sees it too.
        toast.error(`Action applied, but the member notification failed (${r.data.notify_error}). The audit log has the details.`);
      } else if (payload.notify_client && r.data?.client_notified) {
        toast.success("Action applied — member notified by email");
      } else {
        toast.success("Action applied");
      }
      setData((d) => ({ ...d, file: r.data.file }));
      setActionOpen(null);
      setReason(""); setNotes(""); setClientMessage(""); setStatusOverride("");
      setNotifyClient(true); setClientReason("");
      // Refresh audit
      const a = await api.get(`/command-center/admin/files/${userId}/audit`);
      setAudit(a.data.entries || []);
    } catch (e) {
      // gate-002 409s carry a structured detail {message, missing[]} —
      // surface the message, never an [object Object] toast.
      const detail = e.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) || detail?.message || "Action failed"
      );
    } finally { setSubmitting(false); }
  };

  if (loading || !data) {
    return (
      <DashboardLayout title="File · Loading" subtitle="">
        <div className="animate-pulse h-32 bg-slate-100 rounded" />
      </DashboardLayout>
    );
  }

  const f = data.file;
  const u = data.user;
  const activeAction = ACTION_BUTTONS.find((a) => a.id === actionOpen);
  // gate-002: the canonical Step-1 checklist (gate-001, same structure the
  // backend gate enforces). Incomplete items make the handoff dialog demand
  // an override reason and list exactly what's missing.
  const missingChecklist = (data.onboarding_checklist || []).filter((i) => !i.done);
  const handoffGated = actionOpen === "approve_for_partner_handoff" && missingChecklist.length > 0;
  const reasonRequired = activeAction?.needsReason || handoffGated;

  return (
    <DashboardLayout title={`File · ${f.vc_number}`} subtitle={u.full_name || u.email}>
      <Button variant="outline" onClick={() => navigate("/admin/command-center")} className="mb-4 border-slate-300">
        <ChevronLeft size={16} /> Back to all files
      </Button>

      {/* Status hero */}
      <Card className="mb-6 bg-white border-slate-200">
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500"><Hash size={12} className="inline" /> ES-VC#</p>
            <p data-testid="file-vc-number" className="font-mono font-bold text-slate-900">{f.vc_number}</p>
            <p className="text-xs text-slate-500 mt-1">Introducer: {f.legacy_vc || "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Status</p>
            <p className="font-semibold text-slate-900">{f.status}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Priority</p>
            <p className="font-semibold text-slate-900 uppercase">{f.priority_level}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Responsible</p>
            <p className="font-semibold text-slate-900">{f.responsible_party}</p>
            <p className="text-xs text-slate-500">Blocked: {f.blocked_on || "—"}</p>
          </div>
        </CardContent>
      </Card>

      {/* Flags */}
      <div className="flex flex-wrap gap-2 mb-6">
        {f.red_alert && <Badge className="bg-red-100 text-red-900 border-red-300">RED ALERT</Badge>}
        {f.compliance_attention_required && <Badge className="bg-amber-100 text-amber-900 border-amber-300">Compliance Attention</Badge>}
        {f.suitability_review_required && <Badge className="bg-amber-100 text-amber-900 border-amber-300">Suitability Review</Badge>}
        {f.introducer_compliance_review && <Badge className="bg-blue-100 text-blue-900 border-blue-300">Introducer Review</Badge>}
        {f.is_priority_stalled && <Badge className="bg-red-100 text-red-900 border-red-300">Priority Stalled</Badge>}
        {!f.is_priority_stalled && f.is_stalled && <Badge className="bg-amber-100 text-amber-900 border-amber-300">Stalled</Badge>}
        {f.is_high_value && <Badge className="bg-violet-100 text-violet-900 border-violet-300">High Value</Badge>}
        {f.escalated_to_founder && <Badge className="bg-violet-100 text-violet-900 border-violet-300">Escalated · Founder</Badge>}
        {f.escalated_to_compliance && <Badge className="bg-red-100 text-red-900 border-red-300">Escalated · Compliance</Badge>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* MEMBER + INTAKE */}
        <Card className="lg:col-span-2 bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 text-base">Member & Intake</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500">Email:</span> {u.email}</div>
              <div><span className="text-slate-500">Phone:</span> {u.phone || "—"}</div>
              <div><span className="text-slate-500">Role:</span> {u.role}</div>
              <div><span className="text-slate-500">Joined:</span> {(u.created_at || "").slice(0, 10)}</div>
              <div><span className="text-slate-500">KYC:</span> {u.kyc_status}</div>
              <div><span className="text-slate-500">Accredited:</span> {u.accredited_status || "—"}</div>
              <div><span className="text-slate-500">Capital band:</span> {f.estimated_capital_band || "—"}</div>
              <div><span className="text-slate-500">Consent signed:</span> {u.data_transfer_consent_signed_at ? "Yes" : "No"}</div>
            </div>

            {f.red_alert_reasons?.length > 0 && (
              <div className="p-3 rounded border border-red-300 bg-red-50">
                <p className="text-xs font-semibold text-red-900 uppercase mb-1">Red-Alert Reasons</p>
                <ul className="text-xs text-red-900 space-y-1">
                  {f.red_alert_reasons.map((r, i) => <li key={`red-alert-${i}-${(r || '').slice(0,24)}`}>• {r}</li>)}
                </ul>
              </div>
            )}

            {f.intake_capital_readiness && (
              <details className="text-sm" open>
                <summary className="font-semibold text-slate-900 cursor-pointer">Capital Readiness Intake</summary>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  {/* STARSEED-CAPMARKETS-072/073: multi-select fields arrive as
                      arrays (legacy documents as scalars) — join generically. */}
                  {Object.entries(f.intake_capital_readiness).map(([k, v]) => (
                    <div key={k}><span className="text-slate-500">{k.replace(/_/g, " ")}:</span> <span className="text-slate-900">{formatIntakeValue(v) || "—"}</span></div>
                  ))}
                </div>
              </details>
            )}

            {f.intake_capital_safety && (
              <details className="text-sm">
                <summary className="font-semibold text-slate-900 cursor-pointer">Capital Safety Intake</summary>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(f.intake_capital_safety).map(([k, v]) => (
                    <div key={k}><span className="text-slate-500">{k.replace(/_/g, " ")}:</span> <span className="text-slate-900">{v}</span></div>
                  ))}
                </div>
              </details>
            )}

            {f.intake_introducer_statements && (
              <details className="text-sm">
                <summary className="font-semibold text-slate-900 cursor-pointer">Introducer Statements</summary>
                <div className="mt-2 text-xs">
                  <p className="text-slate-500">Selected:</p>
                  <ul className="ml-4 list-disc text-slate-900">
                    {(f.intake_introducer_statements.selected || []).map((s, i) => <li key={`stmt-${i}-${(s || '').slice(0,24)}`}>{s}</li>)}
                    {!(f.intake_introducer_statements.selected || []).length && <li>None</li>}
                  </ul>
                  {f.intake_introducer_statements.other_note && (
                    <p className="mt-2 text-slate-900"><span className="text-slate-500">Other:</span> {f.intake_introducer_statements.other_note}</p>
                  )}
                </div>
              </details>
            )}

            {/* Admin notes */}
            {f.admin_notes?.length > 0 && (
              <div>
                <p className="font-semibold text-slate-900 text-sm mb-2">Admin Notes</p>
                <div className="space-y-2">
                  {f.admin_notes.map((n, i) => (
                    <div key={n.id || i} className="p-2 rounded bg-slate-50 border border-slate-200 text-xs">
                      <p className="text-slate-900">{n.note || n.reason || JSON.stringify(n)}</p>
                      <p className="text-slate-500 mt-1">{n.by_admin || n.by_name} · {n.created_at}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {f.client_facing_notes?.length > 0 && (
              <div>
                <p className="font-semibold text-slate-900 text-sm mb-2">Client-Facing Messages</p>
                <div className="space-y-2">
                  {f.client_facing_notes.map((n, i) => (
                    <div key={n.id || i} className="p-2 rounded bg-blue-50 border border-blue-200 text-xs">
                      <p className="text-slate-900">{n.message}</p>
                      <p className="text-slate-500 mt-1">{n.by_admin} · {n.created_at}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ACTION BUTTONS */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 text-base">Admin Actions</CardTitle>
            <CardDescription className="text-slate-500 text-xs">Every action writes an audit log entry.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {ACTION_BUTTONS.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.id}
                    data-testid={`admin-action-${a.id}`}
                    onClick={() => setActionOpen(a.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded border text-sm font-medium transition ${TONE_BTN[a.tone]}`}
                  >
                    <Icon size={14} /> {a.label}
                  </button>
                );
              })}
            </div>

            {/* Manual status override */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <Label className="text-xs text-slate-700">Manual Status Override</Label>
              <select
                data-testid="admin-status-override"
                value={statusOverride}
                onChange={(e) => setStatusOverride(e.target.value)}
                className="w-full mt-1 p-2 border border-slate-300 rounded text-sm bg-white text-slate-900"
              >
                <option value="">Select a status...</option>
                {ALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button
                data-testid="admin-set-status-btn"
                disabled={!statusOverride || submitting}
                onClick={() => performAction("set_status", { new_status: statusOverride })}
                className="w-full mt-2 bg-slate-900 text-white hover:bg-slate-800"
                size="sm"
              >
                Apply Status
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AUDIT LOG */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-900 text-base">Audit Log · {audit.length} entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {audit.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">No audit entries yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
                  <tr>
                    <th className="text-left p-3">When</th>
                    <th className="text-left p-3">Admin</th>
                    <th className="text-left p-3">Action</th>
                    <th className="text-left p-3">Status Change</th>
                    <th className="text-left p-3">Reason / Notes</th>
                    <th className="text-left p-3">Client Notified</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((e) => (
                    <tr key={e.id} className="border-t border-slate-100">
                      <td className="p-3 text-slate-700 whitespace-nowrap">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="p-3 text-slate-900">{e.admin_name || e.admin_email || "—"}</td>
                      <td className="p-3 text-slate-900 font-medium">{e.action}</td>
                      <td className="p-3 text-slate-700">{e.previous_status || "—"} → {e.new_status || "—"}</td>
                      <td className="p-3 text-slate-700 max-w-xs truncate">{e.reason || e.notes || "—"}</td>
                      <td className="p-3">{e.client_notified ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ACTION DIALOG */}
      <Dialog open={!!actionOpen} onOpenChange={(v) => !v && setActionOpen(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{activeAction?.label}</DialogTitle>
            <DialogDescription className="text-slate-600">
              This action will be applied to {u.full_name || u.email} and recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {handoffGated && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="handoff-gate-warning">
                <p className="text-sm font-semibold text-amber-800">
                  Onboarding incomplete — handoff is gated
                </p>
                <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
                  {missingChecklist.map((i) => (
                    <li key={i.key}>{i.label}</li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-amber-700">
                  Complete the items above, or provide an override reason — the
                  override is recorded in the audit log.
                </p>
              </div>
            )}
            {reasonRequired && (
              <div>
                <Label className="text-slate-900">Reason</Label>
                <Input
                  data-testid="action-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="mt-1 bg-white text-slate-900 border-slate-300"
                  placeholder={handoffGated ? "Override reason (required while items are missing)" : "Why are you taking this action?"}
                />
              </div>
            )}
            {activeAction?.needsNotes && (
              <div>
                <Label className="text-slate-900">Note</Label>
                <Textarea
                  data-testid="action-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="mt-1 bg-white text-slate-900 border-slate-300"
                />
              </div>
            )}
            {activeAction?.needsClientReason && (
              <div>
                <Label className="text-slate-900">Reason shown to the member</Label>
                <Textarea
                  data-testid="action-client-reason"
                  value={clientReason}
                  onChange={(e) => setClientReason(e.target.value)}
                  rows={3}
                  className="mt-1 bg-white text-slate-900 border-slate-300"
                  placeholder="Member-safe wording — the internal reason above is never sent. Required when notifying."
                />
              </div>
            )}
            {activeAction?.notifiable && (
              <label className="flex items-center gap-2 text-sm text-slate-900 select-none" data-testid="notify-client-toggle">
                <input
                  type="checkbox"
                  checked={notifyClient}
                  onChange={(e) => setNotifyClient(e.target.checked)}
                  className="h-4 w-4 accent-slate-900"
                />
                Notify the member by email (recorded in the audit log)
              </label>
            )}
            {activeAction?.needsClientMessage && (
              <div>
                <Label className="text-slate-900">Message to client</Label>
                <Textarea
                  data-testid="action-client-message"
                  value={clientMessage}
                  onChange={(e) => setClientMessage(e.target.value)}
                  rows={4}
                  className="mt-1 bg-white text-slate-900 border-slate-300"
                  placeholder="This message will appear on the client's dashboard."
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionOpen(null)}>Cancel</Button>
            <Button
              data-testid="action-submit-btn"
              onClick={() => performAction(actionOpen)}
              disabled={submitting}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {submitting ? "Applying..." : "Confirm Action"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminCommandCenterFilePage;
